// ── Multi-tenant resolution ───────────────────────────────────────────────────
// طبقة العزل بين الشركات. كل بيانات الشركة تعيش تحت companies/{companyId}/...
//
// مفتاح التفعيل (feature flag): يبقى OFF حتى:
//   1) تُنقل بيانات الشركة الحالية إلى companies/01/...  (scripts/migrate-tenant.js)
//   2) يُفعّل Firebase Auth + custom claims (المرحلة ٢)
//   3) تُنشر قواعد الحماية (firestore.rules)
// عندما يكون OFF يتصرّف النظام تماماً كما هو الآن (مجموعات مسطّحة) — صفر كسر.
export const TENANT_ENABLED = true;

// مفتاح المرحلة ٢ (Firebase Auth): يبقى OFF حتى يُشغَّل سكربت الإعداد
// (scripts/setup-auth.js) ويُفعَّل Firebase Auth في الـ console. عند OFF
// يبقى تسجيل الدخول الحالي (ID + باسورد عبر Firestore) كما هو — صفر كسر.
export const AUTH_ENABLED = true;

// المرحلة ٦: دخول بالإيميل والباسورد عبر Firebase Auth الأصلي. عند true تستخدم
// شاشة الدخول الإيميل بدل رقم المستخدم، والإيميل يحدّد الشركة تلقائياً من الـ claims.
export const EMAIL_AUTH = true;

import { COMPANY_ID_DEFAULT } from "../constants";
import { loadSession } from "./session";

// مُعرّف الشركة النشطة الحالية. يُضبط عند تسجيل الدخول (setActiveCompany)
// ومستقبلاً يأتي من Firebase Auth custom claims (request.auth.token.companyId).
let _activeCompanyId = COMPANY_ID_DEFAULT;

export function setActiveCompany(cid) {
  if (cid) _activeCompanyId = String(cid);
}

export function getActiveCompany() {
  return _activeCompanyId;
}

export function resetActiveCompany() {
  _activeCompanyId = COMPANY_ID_DEFAULT;
}

// ── عملة الشركة الأساسية (base currency) ───────────────────────────────────────
// تُختار مرة واحدة عند إنشاء الشركة وتُخزَّن في companies/{cid}/meta/info.baseCurrency.
// تُحمّل عند الدخول (نفس مكان تحميل features في App.js) وتُقرأ عبر getBaseCurrency().
// الشركات القديمة (بلا الحقل) → null → getBaseCurrency() يرجّع KWD (توافق رجعي كامل).
// نخزّن السلسلة كما هي؛ التحقق/التطبيع يتم في طبقة currency.js عند التنسيق.
let _baseCurrency = null;

export function setBaseCurrency(code) {
  _baseCurrency = code || null;
}

export function getBaseCurrency() {
  return _baseCurrency || "KWD";
}

export function resetBaseCurrency() {
  _baseCurrency = null;
}

// ── اسم المتجر العام (Store name) ───────────────────────────────────────────────
// الاسم التجاري للمتجر (من إعدادات المتجر: bekvam/website_settings.storeName).
// يُستخدم لبناء رابط معاينة المتجر /store/<اسم>--<رقم>. يُحمّل عند الدخول (نفس مكان
// تحميل features/baseCurrency في App.js) ويُقرأ عبر storePreviewPath().
// السبب: قبل هذا كان الرابط يأخذ اسم المستخدم المسجّل من الجلسة، فلو دخل موظف ظهر
// اسمه (لا اسم الشركة) في الرابط. اسم المتجر هو الاسم الصحيح دائماً.
let _storeName = null;

export function setStoreName(name) {
  _storeName = name || null;
}

export function getStoreName() {
  return _storeName || "";
}

export function resetStoreName() {
  _storeName = null;
}

// توقيع الرسائل الجاهزة التي تُفتح للعميل على واتساب (روابط wa.me).
// كان التوقيع مثبّتاً في الكود («مع تحيات فريق Bekvam») في صفحة تفاصيل الطلب
// وتطبيق الموظف، فيصل عميلَ أي شركة موقّعاً باسم شركة أخرى. اسم المتجر محمّل
// أصلاً عند الدخول (setStoreName في App.js) فلا طلب شبكة هنا. عند غياب الاسم
// نوقّع بصيغة محايدة بدل أي اسم ثابت.
export function storeSignature(lang = "ar") {
  const name = getStoreName();
  if (lang === "en") return name ? `Best regards, ${name}` : "Best regards";
  return name ? `مع تحيات فريق ${name}` : "مع تحياتنا";
}

// اسم آمن للرابط: مسافات → شرطة، وإزالة المحارف التي تكسر المسار أو تتعارض مع الفاصل "--".
function _slugifyName(name) {
  return String(name || "")
    .trim()
    .replace(/[\\/?#%]+/g, "")  // محارف تكسر المسار
    .replace(/\s+/g, "-")
    .replace(/-{2,}/g, "-")      // "--" محجوز كفاصل بين الاسم والرقم
    .replace(/^-+|-+$/g, "");
}

// مسار معاينة متجر الشركة النشطة من لوحة الإدارة (على دومين المنصّة):
//   /store/<اسم-الشركة>--<رقم-الشركة>
// رقم الشركة (companyId) يضمن فتح المتجر الصحيح حتى لو تشابهت أسماء الشركات.
// بدون اسم متاح: /store/<رقم-الشركة>.
export function storePreviewPath() {
  const cid = getActiveCompany();
  const sess = loadSession();
  // اسم المتجر العام (من إعدادات المتجر) هو الاسم الصحيح للرابط، لا اسم المستخدم
  // المسجّل: اسم الموظف ليس اسم الشركة فلا يجوز تسريبه في الرابط. نرجع لاسم الجلسة
  // فقط لمدير الشركة (role==="company") كتوافق رجعي، وإلا نكتفي برقم الشركة.
  const raw = getStoreName() || (sess?.role === "company" ? sess?.name : "");
  const name = _slugifyName(raw);
  return name ? `/store/${name}--${cid}` : `/store/${cid}`;
}

// الرابط العام الكامل لمتجر الشركة — مصدر واحد لكل ما ينشره (اللوحة، رابط
// اللعبة، أي مشاركة لاحقة). دومين التاجر المربوط يسبق دائماً: رابط على نطاق
// المنصّة يُفقِد التاجر هويته أمام زبونه، وهو غير مفهرس أصلاً (noindex في
// StorefrontGate)، فلا يصلح رابطاً يُنشر. بلا دومين مربوط نرجع لرابط المعاينة.
// يُمرَّر storeDomain من ecommerce_config الذي تقرأه الشاشة أصلاً — بلا قراءة زائدة.
export function storePublicUrl(storeDomain) {
  const dom = String(storeDomain || "").trim().replace(/^https?:\/\//, "").replace(/\/+$/, "");
  if (dom) return `https://${dom}`;
  return `${window.location.origin}${storePreviewPath()}`;
}

// رابط صفحة منتج في المتجر — نفس مسار Storefront (pagePath: `/product/<id>`) فوق
// storePublicUrl، فيتبع الدومين تلقائياً: يُحسب وقت العرض من storeDomain الحالي
// ولا يُخزَّن على المنتج، فتغيير الدومين (أو ربطه لأول مرة) يغيّر كل الروابط فوراً.
export function storeProductUrl(storeDomain, productId) {
  const id = String(productId ?? "").trim();
  return id ? `${storePublicUrl(storeDomain)}/product/${encodeURIComponent(id)}` : "";
}

// ── رابط الفاتورة العام الذي يصل للعميل — مصدر واحد ───────────────────────────
// يقابله على السيرفر api/_invoiceLink.js (نفس الشكل حرفياً) — أي تغيير هنا يُنقل
// هناك، فالرابط الواحد قد يُبنى من الطرفين لنفس الفاتورة.
//
// لماذا دومين ثابت لا window.location.origin: هذا رابط **يُرسل للعميل ويبقى**،
// فلا يجوز أن يتبع الدومين الذي صادف أن التاجر فاتح عليه اللوحة (قد يكون دومين
// متجره الذي يغيّره لاحقاً، أو نشرة معاينة vercel.app مؤقتة). الدومين القديم
// my.bekvamkw.com يبقى عاملاً لمن يفتح اللوحة عليه — لكن ما يخرج للعميل يحمل
// اسم البراند دائماً (نفس مبدأ PLATFORM_ORIGIN في SuperAdminApp).
export const PLATFORM_ORIGIN = "https://my.velorci.com";

// ⛔️ `id=` آخر بارامتر دائماً: زر قالب واتساب (URL_DYNAMIC) يُلحِق الـ linkId في
// آخر الرابط عند ميتا، فأي بارامتر بعده يضيع. و`c=` إلزامي — أكشن invoice-public
// يحلّ الشركة منه وحده، وبلا وجوده تُقرأ فاتورة الشركة الافتراضية (أو لا شيء).
export function invoicePublicBase(cid = getActiveCompany()) {
  const c = String(cid || "").replace(/[^a-zA-Z0-9_-]/g, "");
  return `${PLATFORM_ORIGIN}/api/invoice-proxy?action=invoice-public`
    + (c ? `&c=${encodeURIComponent(c)}` : "") + "&id=";
}

export function invoicePublicUrl(linkId, cid = getActiveCompany()) {
  return invoicePublicBase(cid) + encodeURIComponent(String(linkId || ""));
}

// ── مميزات الشركة (Feature flags / Modules) ────────────────────────────────────
// تحكّم بظهور مميزات معيّنة لكل شركة على حدة. يضبطها مالك المنصة (السوبر-أدمن) من
// لوحته، وتُخزّن في companies/{cid}/meta/info.features. تُحمّل عند الدخول وتُقرأ عبر
// hasFeature(). الشركات القديمة (بلا حقل features) تُعامَل بأن كل المميزات مفعّلة
// (توافق رجعي — صفر كسر)، والشركات الجديدة تبدأ بالمميزات المتقدّمة مطفأة.

// وضع التعطيل (featureModes): لكل ميزة معطّلة اختياران —
//   "hide" (الافتراضي) = تختفي من النظام تماماً كأنها غير موجودة.
//   "lock"             = تبقى ظاهرة بقفل 🔒 ولا تُفتح (تشويق/ترقية الباقة).
// يُخزَّن في companies/{cid}/meta/info.featureModes بجانب features، ولا معنى له
// إلا للميزة المعطّلة. أُبقيت `features` بوليانية كما هي حفاظاً على التوافق.

// قائمة الموديولات القابلة للتفعيل/التعطيل لكل شركة (تُستخدم في واجهة السوبر-أدمن).
// المجموعة (group) للعرض فقط: "core" = عمود التجارة (المتجر/المنتجات/الطلبات) الذي
// يميّز نشاطاً تجارياً عن نشاط خدمات، و"extra" = الموديولات المتقدّمة فوقه.
// ⚠️ أي مفتاح يُضاف هنا يجب أن يُضاف في ALLOWED داخل أكشن sa-update-company-features
// (api/domain-manage.js) وإلا سقط صامتاً عند الحفظ وبقي الموديول مفعّلاً أبداً.
export const GATED_FEATURES = [
  { key: "store",      icon: "🛍️", label: "المتجر الإلكتروني", group: "core" },
  { key: "products",   icon: "📦", label: "المنتجات",          group: "core" },
  { key: "orders",     icon: "🧾", label: "الطلبات",           group: "core" },
  { key: "dashboard",  icon: "🏠", label: "الرئيسية (الداشبورد)", group: "extra" },
  { key: "reports",    icon: "📊", label: "التقارير",          group: "extra" },
  { key: "tailor",     icon: "✂️", label: "نظام الخياط",       group: "extra" },
  { key: "packer",     icon: "📦", label: "نظام التعبئة",      group: "extra" },
  { key: "ads",        icon: "📣", label: "الإعلانات",         group: "extra" },
  { key: "accounting", icon: "🧮", label: "المحاسبة",          group: "extra" },
  { key: "costs",      icon: "💰", label: "تكاليف المنتجات",   group: "extra" },
  { key: "purchases",  icon: "🛒", label: "المشتريات",         group: "extra" },
  { key: "marketing_calendar", icon: "🗓️", label: "التقويم التسويقي", group: "extra" },
  { key: "loyalty",    icon: "🎁", label: "نظام الولاء",       group: "extra" },
  { key: "game",       icon: "🎮", label: "لعبة الجوائز",      group: "extra" },
];

// ── نوع النشاط (businessType) ──────────────────────────────────────────────────
// حقل واحد في companies/{cid}/meta/info.businessType يصف شكل النشاط، ومنه يُشتقّ
// **بريسِت** جاهز لمفاتيح features أعلاه. ليس مساراً ثانياً في الكود ولا آلية عزل
// جديدة: النظام كله يبقى يقرأ hasFeature() وحدها، والنوع مجرّد طريقة مريحة لضبط
// عدّة مفاتيح دفعة واحدة عند إنشاء الحساب، وللواجهة كي تسمّي الأشياء بلغة صاحبها.
//
//   retail   — تاجر: يبيع منتجات (السلوك الحالي بالكامل).
//   services — مزوّد خدمات: موقع تعريفي + CRM + واتساب، بلا متجر/منتجات/طلبات.
//
// الشركات القائمة بلا الحقل = "retail" (توافق رجعي كامل — صفر كسر).
//
// ⚠️ هنا المفاتيح وحدها. كتالوج العرض (الأسماء والأوصاف) والبريسِت يعيشان في
// src/features/company/businessTypes.js لأن شاشة السوبر-أدمن وحدها تحتاجهما،
// وهذا الملف يُحمَّل في أول تحميل للمتجر العام — فلا نُثقله بنصوص لوحة إدارة.
export const BUSINESS_TYPE_KEYS = ["retail", "services"];

export const DEFAULT_BUSINESS_TYPE = "retail";

export const isKnownBusinessType = (k) => BUSINESS_TYPE_KEYS.includes(k);

let _businessType = null; // null = لم يُحمّل بعد أو شركة قديمة بلا الحقل

export function setBusinessType(k) {
  _businessType = isKnownBusinessType(k) ? k : null;
}

export function resetBusinessType() {
  _businessType = null;
}

/** نوع النشاط الحالي — الشركة بلا الحقل تُعامَل تاجراً (السلوك السابق). */
export function getBusinessType() {
  return _businessType || DEFAULT_BUSINESS_TYPE;
}

/** هل الحساب حساب خدمات؟ للتسمية والنصوص فقط — الإظهار/الإخفاء يبقى بـ hasFeature. */
export const isServicesBusiness = () => getBusinessType() === "services";

let _features = null;       // null = لم تُحمّل بعد
let _featuresLegacy = true; // true = شركة بلا إعداد مميزات → كل شيء مفعّل (توافق رجعي)
let _featureModes = {};     // { [key]: "hide" | "lock" } — للمعطّلة فقط

export function setActiveFeatures(f, modes) {
  if (f && typeof f === "object" && Object.keys(f).length) {
    _features = f;
    _featuresLegacy = false;
  } else {
    _features = {};
    _featuresLegacy = true;
  }
  _featureModes = (modes && typeof modes === "object") ? modes : {};
}

export function resetFeatures() {
  _features = null;
  _featuresLegacy = true;
  _featureModes = {};
}

export function getFeatures() {
  return _features || {};
}

export function getFeatureModes() {
  return _featureModes;
}

// ── موديولات لا تُفتح إلا بقرار صريح ─────────────────────────────────────────
// القاعدة العامة أدناه متساهلة عمداً (المفتاح الغائب = مفعّل)، وهي صحيحة لموديول
// كان موجوداً ثم صار قابلاً للإطفاء. لكنها خاطئة لموديول **جديد يُباع**: مفتاحه
// غائب عن كل شركة قائمة، فالتساهل يعني إعطاءه مجاناً للجميع لحظة النشر — ويعني
// كذلك ظهور شاشة جديدة فجأة في لوحة تاجر لم يطلبها ولا يعرف ما هي.
// المفتاح الغائب هنا = **مطفأ**، فلا يفتحه إلا مالك المنصّة صراحةً لكل شركة.
// ⚠️ لا تضِف مفتاحاً قائماً إلى هذه المجموعة: ستُطفئه فوراً عن كل شركة تستعمله.
export const OPT_IN_FEATURES = new Set(["game"]);

// هل الميزة مفعّلة للشركة الحالية؟ الشركات القديمة (legacy) كل مميزاتها مفعّلة.
// ومفتاح لم يُضبط بعد = مفعّل: أي ميزة تُضاف للقائمة لاحقاً لا تختفي فجأة عن
// الشركات القائمة التي أُعِدّت قبل وجودها — تعطيلها قرار صريح من مالك المنصة.
// (يُستثنى من ذلك OPT_IN_FEATURES أعلاه — عكس القاعدة تماماً.)
export function hasFeature(key) {
  if (OPT_IN_FEATURES.has(key)) return _features?.[key] === true;
  if (_featuresLegacy) return true;
  if (!_features || !(key in _features)) return true;
  return !!_features[key];
}

// حالة الميزة الكاملة: "on" مفعّلة · "locked" معطّلة لكن ظاهرة بقفل · "hidden" مخفية.
export function featureState(key) {
  if (hasFeature(key)) return "on";
  return _featureModes[key] === "lock" ? "locked" : "hidden";
}

/** معطّلة لكن تبقى ظاهرة بقفل — تُعرض ولا تُفتح. */
export const isFeatureLocked = (key) => featureState(key) === "locked";

/** هل تظهر الميزة في الواجهة أصلاً؟ (مفعّلة أو مقفولة) — المخفية لا تظهر إطلاقاً. */
export const featureVisible = (key) => featureState(key) !== "hidden";

// ── باقة الحساب واشتراكه (للعرض في قائمة الشركة) ───────────────────────────────
// يضبطهما مالك المنصة، ويُخزّنان في companies/{cid}/meta/info.{package,subscription}
// (وثيقة خاصة بالشركة، ليست عامة). تُحمّل مع features عند الدخول — بلا طلب إضافي.
let _plan = null; // { package:{id,name,image}|null, subscription:{price,months,startedAt,expiresAt}|null }

export function setActivePlan(p) {
  _plan = p && typeof p === "object" ? p : null;
}

export function resetPlan() {
  _plan = null;
}

export function getActivePlan() {
  return _plan;
}
